import { computed, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { DEFAULT_PAGE_SIZE, PageMeta } from '@/app/core/models/paged-result.model';
import { HospitalAccount, HospitalAccountFilters } from '../models/hospital-account.model';
import { HospitalAccountRepository } from '../services/hospital-account.repository';

export type HospitalAccountsStatus = 'loading' | 'ready' | 'empty' | 'error';

const ERROR_SUBJECT = 'las cuentas hospitalarias';

const emptyPage = (size: number): PageMeta => ({
  page: 0,
  size,
  totalElements: 0,
  totalPages: 1,
});

@Injectable()
export class HospitalAccountsStore {
  private readonly repository = inject(HospitalAccountRepository);

  private readonly _status = signal<HospitalAccountsStatus>('loading');
  private readonly _records = signal<readonly HospitalAccount[]>([]);
  private readonly _size = signal(DEFAULT_PAGE_SIZE);
  private readonly _pageMeta = signal<PageMeta>(emptyPage(DEFAULT_PAGE_SIZE));
  private readonly _filters = signal<HospitalAccountFilters>({});
  private readonly _error = signal<AppError | null>(null);
  private inFlight?: Subscription;

  readonly status = this._status.asReadonly();
  readonly records = this._records.asReadonly();
  readonly pageMeta = this._pageMeta.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly error = this._error.asReadonly();

  readonly isLoading = computed(() => this._status() === 'loading');
  readonly hasResults = computed(() => this._status() === 'ready');

  search(filters: HospitalAccountFilters): void {
    this._filters.set(filters);
    this.load(0);
  }

  changeSize(size: number): void {
    this._size.set(size);
    this.load(0);
  }

  load(page = 0): void {
    // Cancelar la carga anterior evita que una respuesta tardía (p. ej. de la institución previa) pise a la actual.
    this.inFlight?.unsubscribe();
    this._error.set(null);
    this._status.set('loading');

    this.inFlight = this.repository
      .search(this._filters(), { page, size: this._size() })
      .subscribe({
        next: (result) => {
          this._records.set(result.content);
          this._pageMeta.set(result.page);
          this._status.set(result.page.totalElements === 0 ? 'empty' : 'ready');
        },
        error: (cause: unknown) => {
          this._records.set([]);
          this._pageMeta.set(emptyPage(this._size()));
          this._error.set(toAppError(cause, ERROR_SUBJECT));
          this._status.set('error');
        },
      });
  }
}
